import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileText, Image as ImageIcon, Film, Presentation, Package, Palette, Search, Download as DownloadIcon, Trash2, Plus, X } from "lucide-react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ICONS = {
  PDF: FileText,
  Images: ImageIcon,
  Videos: Film,
  Presentations: Presentation,
  "Brand Assets": Palette,
  "Marketing Material": Package,
};

function bytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${u[i]}`;
}

export default function Downloads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "PDF", file: null, meta: null });
  const fileRef = useRef(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  const load = () => {
    setLoading(true);
    api
      .get("/downloads", { params: { q: q || undefined, category: category !== "all" ? category : undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/downloads/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(load, [q, category]);

  const download = async (item) => {
    try {
      const token = localStorage.getItem("em_os_token");
      const url = `${API_BASE}/files/${item.file_path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
      const r = await fetch(url, { credentials: "include" });
      if (r.status === 401) {
        toast.error("Your session has expired. Please sign in again.");
        setTimeout(() => {
          localStorage.removeItem("em_os_token");
          window.location.assign("/login");
        }, 1500);
        return;
      }
      if (r.status === 404) {
        toast.error("This file is no longer available. Please contact support.");
        return;
      }
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const bUrl = URL.createObjectURL(blob);
      a.href = bUrl;
      a.download = item.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(bUrl);
    } catch (e) {
      toast.error("Download failed — check your connection and try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("adminDownloads.confirmDelete"))) return;
    try {
      await api.delete(`/downloads/${id}`);
      toast.success(t("adminDownloads.downloadDeleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, file, title: f.title || file.name }));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, meta: r.data }));
      toast.success(t("adminDownloads.downloadCreated"));
    } catch (err) {
      toast.error(formatApiError(err));
      setForm((f) => ({ ...f, file: null }));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.meta) {
      toast.error(t("errors.generic"));
      return;
    }
    try {
      await api.post("/downloads", {
        title: form.title,
        description: form.description,
        category: form.category,
        file_path: form.meta.path,
        file_size: form.meta.size,
        content_type: form.meta.content_type,
        original_filename: form.meta.original_filename,
      });
      toast.success(t("adminDownloads.downloadCreated"));
      setShowForm(false);
      setForm({ title: "", description: "", category: "PDF", file: null, meta: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{t("dashboard.module")}</p>
          <h1 className="font-display text-4xl font-light tracking-tight">{t("downloads.title")}</h1>
          <p className="mt-2 text-sm text-zinc-500">{t("downloads.description")}</p>
        </div>
        {user?.role === "admin" && (
          <button
            data-testid="new-download-btn"
            onClick={() => setShowForm(true)}
            className="bg-gold text-ink-0 font-semibold rounded-lg px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-gold-hover"
            style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
          >
            <Upload className="w-4 h-4" /> {t("adminDownloads.uploadFile")}
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <input data-testid="downloads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("downloads.searchPlaceholder")} className="em-input pl-11" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setCategory("all")} className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium ${category === "all" ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`} style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}>{t("common.all")}</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${category === c ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`} style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}>{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center">
          <Package className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-display text-xl mb-2">{t("downloads.noFiles")}</h3>
          <p className="text-sm text-zinc-500">{t("downloads.description")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => {
            const Icon = ICONS[item.category] || FileText;
            return (
              <div key={item.id} data-testid={`file-${item.id}`} className="em-card em-card-hover p-5 flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-gold" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{item.title}</div>
                  {item.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{item.description}</div>}
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2">
                    {item.category} · {bytes(item.file_size)}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => download(item)}
                    data-testid={`download-${item.id}`}
                    className="p-2 rounded-lg bg-white/5 hover:bg-gold/15 hover:text-gold text-zinc-400"
                    style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
                    title="Download"
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </button>
                  {user?.role === "admin" && (
                    <button onClick={() => remove(item.id)} className="p-2 rounded-lg bg-white/5 hover:bg-red-900/40 hover:text-red-200 text-zinc-500" style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="em-card p-8 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">{t("adminDownloads.uploadFile")}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("common.type")}</label>
                <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="file-input" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full em-input flex items-center justify-center gap-2 py-6 border-dashed hover:border-gold cursor-pointer disabled:opacity-50"
                  style={{ transitionProperty: "border-color", transitionDuration: "180ms" }}
                >
                  <Upload className="w-4 h-4 text-gold" />
                  <span className="text-sm">
                    {uploading ? t("common.loading") : form.meta ? form.meta.original_filename : t("adminDownloads.uploadFile")}
                  </span>
                </button>
              </div>
              <input required placeholder={t("common.title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" />
              <textarea placeholder={t("common.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="em-input" rows={3} />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="em-input">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button type="submit" data-testid="download-submit" disabled={uploading || !form.meta} className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-5 py-2.5 hover:bg-gold-hover disabled:opacity-50" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
                {t("common.save")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
